import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../config/api';
import { COLORS, FONTS, BTN_GOLD_STYLE } from './theme';
import Shimmer from './Shimmer';
import { trackEvent } from '../../utils/analytics';

const SHARE_TEXT = encodeURIComponent(
  'Found something I had to share — Paathshaala turns our old Jain stories into audio that holds a mirror to what our kids are going through today. No screens, no scrolling. Register here: https://withvidya.ai/paathshaala'
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const STORAGE_KEY = 'paathshaala_email';

const markRegistered = () => {
  window.dispatchEvent(new CustomEvent('paathshaala:registered'));
};

const PaathshaalaCTA: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Restore registered state on refresh
  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) {
        setSubmitted(true);
        markRegistered();
      }
    } catch {
      // storage unavailable — ignore
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError(true);
      return;
    }
    setError(false);

    setIsLoading(true);
    try {
      await api.post('/api/paathshala/waitlist', { email: trimmed });
    } catch {
      // Endpoint may not exist yet — show success anyway for vapor test
    }
    setIsLoading(false);
    try {
      localStorage.setItem(STORAGE_KEY, trimmed);
    } catch {
      // storage unavailable — ignore
    }
    setSubmitted(true);
    markRegistered();
    trackEvent('paathshala_waitlist_subscribe', { email: trimmed });
  };

  return (
    <section id="register" className="pt-[86px] pb-24">
      <div className="w-full max-w-[640px] md:max-w-[760px] mx-auto px-[26px] md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '0px 0px -8% 0px' }}
          transition={{ duration: 1, ease: [0.22, 0.61, 0.36, 1] }}
          className="relative rounded-md text-center p-[44px_28px_40px]"
          style={{
            background: `linear-gradient(175deg, ${COLORS.parchmentDark}, ${COLORS.parchmentDarker})`,
            border: `1.5px solid ${COLORS.goldLeaf}`,
            boxShadow:
              '0 0 0 5px rgba(197,165,90,0.13), 0 28px 64px -20px rgba(26,15,10,0.5), inset 0 1px 0 rgba(255,244,214,0.5)',
          }}
        >
          {/* Inner manuscript rule */}
          <div
            aria-hidden="true"
            className="absolute inset-2 rounded-[3px] pointer-events-none"
            style={{ border: '1px solid rgba(168,137,62,0.35)' }}
          />

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 0.61, 0.36, 1] }}
              className="relative z-[1]"
            >
              <div
                className="w-[52px] h-[52px] mx-auto mb-4 rounded-full flex items-center justify-center text-2xl"
                style={{
                  border: `1.5px solid ${COLORS.goldLeaf}`,
                  color: COLORS.goldLeafDark,
                  backgroundColor: 'rgba(197,165,90,0.12)',
                }}
              >
                &#10003;
              </div>
              <h3
                className="text-[26px] font-bold mb-2"
                style={{ fontFamily: FONTS.heading, color: COLORS.nearBlack }}
              >
                You&rsquo;re on the list.
              </h3>
              <p
                className="text-[17px] mb-1.5 [text-wrap:pretty]"
                style={{ fontFamily: FONTS.body, color: COLORS.brown }}
              >
                We&rsquo;ll email you the moment the first episodes drop.
              </p>
              <p
                className="text-[17px] italic mt-[18px] mb-3.5"
                style={{ fontFamily: FONTS.body, color: COLORS.brown }}
              >
                Know another parent who&rsquo;d love this?
              </p>
              <a
                href={`https://wa.me/?text=${SHARE_TEXT}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('paathshala_share_click', { channel: 'whatsapp' })}
                className="inline-flex items-center justify-center gap-2.5 min-h-[50px] px-[26px] py-3 rounded-[3px] text-base font-bold tracking-[0.08em] transition-colors hover:bg-[#8B1A1A12]"
                style={{
                  fontFamily: FONTS.body,
                  color: COLORS.redInk,
                  border: '1.5px solid rgba(139,26,26,0.5)',
                }}
              >
                Share on WhatsApp
              </a>
            </motion.div>
          ) : (
            <div className="relative z-[1]">
              <h2
                className="font-bold mb-2.5"
                style={{
                  fontFamily: FONTS.heading,
                  color: COLORS.redInk,
                  fontSize: 'clamp(30px, 8vw, 38px)',
                }}
              >
                Register for access
              </h2>
              <p
                className="text-[17px] mb-7 [text-wrap:pretty]"
                style={{ fontFamily: FONTS.body, color: COLORS.brown }}
              >
                First series dropping soon.
              </p>

              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(false);
                  }}
                  placeholder="Your email address"
                  required
                  id="reg-email"
                  aria-label="Your email address"
                  aria-invalid={error}
                  className="w-full min-h-[54px] px-[18px] py-3.5 rounded-[3px] text-center outline-none transition-shadow placeholder:italic"
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: '17px', // ≥16px prevents iOS Safari auto-zoom on focus
                    color: COLORS.nearBlack,
                    backgroundColor: 'rgba(228,209,175,0.85)',
                    border: `1px solid ${error ? COLORS.redInk : 'rgba(93,64,55,0.45)'}`,
                    boxShadow: error ? '0 0 0 3px rgba(139,26,26,0.18)' : undefined,
                  }}
                  onFocus={(e) => {
                    if (!error) {
                      e.target.style.borderColor = COLORS.goldLeafDark;
                      e.target.style.boxShadow = '0 0 0 3px rgba(197,165,90,0.3)';
                    }
                  }}
                  onBlur={(e) => {
                    if (!error) {
                      e.target.style.borderColor = 'rgba(93,64,55,0.45)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                />
                {error && (
                  <p
                    role="alert"
                    className="text-[15px] italic"
                    style={{ fontFamily: FONTS.body, color: COLORS.redInk }}
                  >
                    That doesn&rsquo;t look like an email &mdash; one more try?
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="relative overflow-hidden inline-flex items-center justify-center w-full px-[30px] py-3.5 cursor-pointer transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60"
                  style={BTN_GOLD_STYLE}
                >
                  <Shimmer />
                  {isLoading ? 'Saving your spot…' : 'Register for access'}
                </button>
              </form>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default PaathshaalaCTA;
